const { isUndefined, keysIn, size } = require('lodash');

const ADD_CREDIT_CARD_ID = 'CreditCard:AddCreditCard';

class DigitalPayPaymentDistributionService {
    constructor() {
        this._orderTotal = 0;
        this._remainingAmount = 0;
        this._paymentDistributions = {};
        this._singleSelectKey = undefined;
        this.AddCreditCardId = ADD_CREDIT_CARD_ID;
    }

    /**
     * Initialise Distribution Service with the given remaining order total.
     * @param {Double} totalOrderAmountStillToPay
     */
    init(totalOrderAmountStillToPay) {
        // Set remaining amount to pay for the order.
        this._orderTotal = this.convertValueToTwoDecimalPlaces(totalOrderAmountStillToPay);

        // Static remaining amount
        this._remainingAmount = this.convertValueToTwoDecimalPlaces(totalOrderAmountStillToPay);

        // Reset distributions
        this._paymentDistributions = {};

        // SingleSelectKey represents the key associated with a payment which always takes the remaining amount to pay.
        this._singleSelectKey = undefined;
    }

    getOrderTotal() {
        return this._orderTotal;
    }

    /**
     * Get remaining amount still due for the current order. Excludes single select type amounts
     */
    getRemainingAmount() {
        return this._remainingAmount;
    }

    convertValueToTwoDecimalPlaces(value) {
        const numberValue = parseFloat(value);
        return numberValue ? Math.round(value * 100) / 100 : 0.00;
    }

    /**
     * Get all keys currently distributed the total amount.
     */
    getKeys() {
        if (!this._paymentDistributions) return [];
        return keysIn(this._paymentDistributions);
    }

    getSingleSelectKey() {
        return this._singleSelectKey;
    }

    isPayingWithNewCard() {
        return this._singleSelectKey === this.AddCreditCardId;
    }

    /**
     * Get payment distribution associated with the given key.
     * @param {String} key
     */
    getPayment(key) {
        return this._paymentDistributions[key] || 0.00;
    }

    /**
     * Add key/value pair representing the distributed amount associated with the given key.
     * @param {string} key
     * @param {boolean} amount
     */
    addPayment(key, amount) {
        if (!key || key === '') return;

        if (key in this._paymentDistributions) {
            this._remainingAmount += this._paymentDistributions[key];
        }

        // TODO: we may allow the user to set a 0 remaining amount amount because addPaymentInFull allows this as well just to keep things consistent
        this._paymentDistributions[key] = this.convertValueToTwoDecimalPlaces(amount);

        this._remainingAmount = this.convertValueToTwoDecimalPlaces(this._remainingAmount - this._paymentDistributions[key]);

        this._updateSingleSelectStagedAmount();
    }

    /**
     * Add staged payment distribution in full for the remaining amount.
     * Note: Payment distribution for this key updates when remaining amount changes.
     * @param {string} key
     */
    addPaymentInFull(key) {
        // Remove existing single select distribution amount.
        delete this._paymentDistributions[this._singleSelectKey];

        // Store single select key.
        if (key) this._singleSelectKey = key;

        // Set remaining amount.
        this._paymentDistributions[key] = this._remainingAmount < 0.00
            ? 0.00
            : this._remainingAmount;
    }

    /**
     * Remove a payment amount association when given the key.
     * @param {string} key
     */
    removePayment(key) {
        if (!key || key === '') return;
        if (key === this._singleSelectKey) {
            this._singleSelectKey = undefined;
        } else if (key in this._paymentDistributions) {
            // Ignore single select types
            this._remainingAmount = this.convertValueToTwoDecimalPlaces(this._remainingAmount + this._paymentDistributions[key]);
        }
        delete this._paymentDistributions[key];

        this._updateSingleSelectStagedAmount();
    }

    /**
     * Determines whether the distribution service has at least one payment.
     */
    hasPayments() {
        return size(this._paymentDistributions) > 0;
    }

    /**
     * Determines whether the distribution service has a single select type.
     */
    hasSingleSelectType() {
        return !isUndefined(this._singleSelectKey);
    }

    /**
     * Updates the remaining amount associated with a single select key.
     */
    _updateSingleSelectStagedAmount() {
        if (this._singleSelectKey) {
            // Remove currently distributed amount associated with single select key.
            delete this._paymentDistributions[this._singleSelectKey];
            // Re-add with updated remaining total.
            this.addPaymentInFull(this._singleSelectKey);
        }
    }
}

module.exports = DigitalPayPaymentDistributionService;



// WEBPACK FOOTER //
// ./src/digitalpay/services/digital-pay-payment-distribution.service.js